import { PublicSeatIdentity } from './PublicSeatIdentity';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Session, SessionState } from '../session/Session';
import type { ObjectView, SeatView, TableModel, ZoneView as Zone, CounterView } from '../model/table';
import { ownsDecision } from '../model/table';
import { choiceEligible, type ChoiceView } from '../model/choices';
import type { Presentation } from '../presentation/types';
import { PresentationContext, createPresentationApi, usePresentation, type PickTargets } from '../presentation/PresentationContext';
import { Slot } from './ObjectCard';
import { themeCss } from '../presentation/theme';
import { LayoutRenderer } from '../presentation/LayoutRenderer';
import { ZoneView, type ZoneDisplay } from './ZoneView';
import { ChoicePanel } from './ChoicePanel';
import { Inspector } from './Inspector';
import { EventLog } from './EventLog';
import { BoardView } from './BoardView';
import { ResultPanel } from './ResultPanel';
import { TopBar } from './TopBar';
import { seatColorVar } from './seatColors';
import { PlayerArea } from './PlayerArea';
import { useReportGameView } from '../issues/IssueContext';
import { useDraftContext } from '../play/draftContext';

interface Props {
  session: Session;
  state: SessionState;
  table: TableModel;
  choices: ChoiceView[];
  presentation: Presentation | null;
  presentationNotice: string | null;
}

/** Everything a layout can place. Generic and presentation layouts draw from the same bricks. */
export interface Bricks {
  table: TableModel;
  seatIndex: Map<string, number>;
  zone: (zone: Zone, display?: ZoneDisplay & { compact?: boolean; large?: boolean; bare?: boolean }) => React.ReactNode;
  board: (zone: Zone, bare?: boolean, zoom?: boolean) => React.ReactNode;
  counters: (counters: CounterView[], label: string, style?: 'chips' | 'table' | 'meters') => React.ReactNode;
  seatHeader: (seat: SeatView) => React.ReactNode;
  status: () => React.ReactNode;
  choices: () => React.ReactNode;
  inspector: () => React.ReactNode;
  log: (limit?: number) => React.ReactNode;
  result: () => React.ReactNode;
}

export function Table(props: Props) {
  const context = useDraftContext({ session: props.session, gameId: props.state.gameId, matchId: props.state.matchId, seat: props.state.seat });
  return <CurrentTable key={context} {...props} />;
}

function CurrentTable({ session, state, table, choices, presentation, presentationNotice }: Props) {
  useReportGameView(state, state.observation, state.revision, presentation);
  const [inspected, setInspected] = useState<string | null>(null);
  const [draftChoiceId, setDraftChoiceId] = useState<string | null>(null);
  const [pickTargets, setPickTargets] = useState<PickTargets | null>(null);
  const eligibleChoices = useMemo(() => choices.filter(choice => choiceEligible(table, choice)), [choices, table]);
  const isMyTurn = (ownsDecision(table) || eligibleChoices.length > 0) && !table.finished && !state.paused && state.connection === 'open';

  useEffect(() => { setDraftChoiceId(id => id && choices.find(c => c.id === id && c.scope === 'auxiliary' && choiceEligible(table, c)) ? id : null); setPickTargets(null); }, [state.revision, state.decisionId]);
  useEffect(() => { if (draftChoiceId && (table.finished || state.paused || !eligibleChoices.some(c => c.id === draftChoiceId))) { session.invalidateChoiceCheck(); setDraftChoiceId(null); setPickTargets(null); } }, [draftChoiceId, table.finished, state.paused, eligibleChoices, session]);
  useEffect(() => { if (inspected && !table.objects.has(inspected)) setInspected(null); }, [table, inspected]);

  const seatIndex = useMemo(() => new Map(table.seats.map((s, i) => [s.id, i])), [table.seats]);
  const selectable = useMemo(() => {
    const ids = new Set<string>();
    if (isMyTurn && !draftChoiceId) for (const c of eligibleChoices) for (const s of c.subjects) ids.add(s);
    return ids;
  }, [eligibleChoices, isMyTurn, draftChoiceId]);
  const attached = useMemo(() => {
    const map = new Map<string, ObjectView[]>();
    for (const o of table.objects.values()) if (o.stack) map.set(o.stack, [...(map.get(o.stack) ?? []), o]);
    return map;
  }, [table]);

  const seatColor = useCallback((owner: string) => {
    const i = seatIndex.get(owner);
    return i === undefined ? null : presentation?.theme.seatColors?.[owner] ?? seatColorVar(i);
  }, [seatIndex, presentation]);
  const api = useMemo(() => createPresentationApi(presentation, table, seatColor, pickTargets), [presentation, table, seatColor, pickTargets]);
  const css = useMemo(() => (presentation ? themeCss(presentation.theme, table.seats.map(s => s.id)) : ''), [presentation, table.seats]);

  const zoneProps = { selectable, inspected, onInspect: setInspected, seatIndex, attached };
  const bricks: Bricks = {
    table, seatIndex,
    zone: (zone, display) => <ZoneView key={zone.id} zone={zone} {...zoneProps} {...display} />,
    board: (zone, bare, zoom) => (bare ? <BoardView key={zone.id} zone={zone} table={table} zoom={zoom} {...zoneProps} /> : (
      <section key={zone.id} className="panel">
        <h2 className="panel-title">{zone.label}</h2>
        <BoardView zone={zone} table={table} zoom={zoom} {...zoneProps} />
      </section>
    )),
    counters: (counters, label, style) => <CounterStrip key={label} counters={counters} label={label} style={style} />,
    seatHeader: seat => (
      <h2 className="player-area-title"><span className="seat-dot" aria-hidden="true" /><PublicSeatIdentity seat={seat} />{seat.isSelf ? ' (you)' : ''}
        {seat.active && !table.finished && <span className="seat-turn">{seat.isSelf ? 'Your turn' : 'Taking a turn'}</span>}
        {seat.status && <span className="seat-status">{seat.status}</span>}
      </h2>
    ),
    status: () => <SeatStrip key="status" table={table} seatIndex={seatIndex} />,
    choices: () => (
      <ChoicePanel key="choices" session={session} state={state} table={table} choices={eligibleChoices} isMyTurn={isMyTurn}
        draftChoiceId={draftChoiceId} onDraft={setDraftChoiceId} onInspect={setInspected} onPickTargets={setPickTargets} />
    ),
    inspector: () => (
      <Inspector key="inspector" table={table} objectId={inspected} choices={isMyTurn ? eligibleChoices : []}
        onClose={() => setInspected(null)} onChoose={id => setDraftChoiceId(id)} />
    ),
    log: limit => <EventLog key="log" entries={limit ? state.log.slice(-limit) : state.log} />,
    result: () => (table.finished ? <ResultPanel key="result" table={table} /> : null),
  };

  return (
    <PresentationContext.Provider value={api}>
      {css && <style>{css}</style>}
      <div className="app-shell">
        <TopBar state={state} table={table} />
        {presentationNotice && <p className="presentation-notice" role="note">{presentationNotice}</p>}
        {presentation ? <LayoutRenderer presentation={presentation} bricks={bricks} /> : <GenericLayout bricks={bricks} />}
      </div>
    </PresentationContext.Provider>
  );
}

/** The generic layout (presentation contract section 8). */
function GenericLayout({ bricks }: { bricks: Bricks }) {
  const { table, seatIndex } = bricks;
  const boards = table.zones.filter(z => z.placement === 'board');
  const publicZones = table.zones.filter(z => z.placement === 'public');
  const selfZones = table.zones.filter(z => z.placement === 'self').sort((a, b) => Number(a.kind === 'hand') - Number(b.kind === 'hand'));
  const opponents = table.seats.filter(s => !s.isSelf);
  const sharedCounters = table.counters.filter(c => c.owner === null);
  const selfSeat = table.seats.find(s => s.isSelf) ?? null;
  return (
    <>
      {bricks.status()}
      <div className="table-layout">
        <main className="table-center" aria-label="Table">
          {bricks.result()}
          {boards.map(z => bricks.board(z))}
          {sharedCounters.length > 0 && bricks.counters(sharedCounters, 'Shared')}
          {publicZones.map(z => bricks.zone(z))}
          {opponents.length > 0 && (
            <div className="opponents">
              {opponents.map(seat => (
                <PlayerArea key={seat.id} seat={seat} seatIndex={seatIndex} label={seat.label}>
                  {bricks.seatHeader(seat)}
                  {bricks.counters(table.counters.filter(c => c.owner === seat.id), `${seat.label} counters`)}
                  {table.zones.filter(z => z.placement === 'opponent' && z.owner === seat.id).map(z => bricks.zone(z, { compact: true }))}
                </PlayerArea>
              ))}
            </div>
          )}
          {selfSeat && (
            <PlayerArea seat={selfSeat} seatIndex={seatIndex} label="Your area">
              {bricks.seatHeader(selfSeat)}
              {bricks.counters(table.counters.filter(c => c.owner === selfSeat.id), 'Your counters')}
              {selfZones.map(z => bricks.zone(z, { large: z.kind === 'hand' }))}
            </PlayerArea>
          )}
        </main>
        <aside className="table-side" aria-label="Moves and details">
          {bricks.choices()}
          {bricks.inspector()}
          {bricks.log()}
        </aside>
      </div>
    </>
  );
}

function SeatStrip({ table, seatIndex }: { table: TableModel; seatIndex: Map<string, number> }) {
  return (
    <ol className="seat-strip" aria-label="Players">
      {table.seats.map(seat => (
        <li key={seat.id} className={`seat-chip${seat.active ? ' is-active' : ''}${seat.isSelf ? ' is-self' : ''}`}
          style={{ '--seat-color': seatColorVar(seatIndex.get(seat.id) ?? 0) } as React.CSSProperties}>
          <span className="seat-dot" aria-hidden="true" />
          <span className="seat-name"><PublicSeatIdentity seat={seat} />{seat.isSelf ? ' (you)' : ''}</span>
          {seat.active && !table.finished && <span className="seat-turn">{seat.isSelf ? 'Your turn' : 'Taking a turn'}</span>}
          {seat.status && <span className="seat-status">{seat.status}</span>}
        </li>
      ))}
    </ol>
  );
}

function CounterStrip({ counters, label, style = 'chips' }: { counters: CounterView[]; label: string; style?: 'chips' | 'table' | 'meters' }) {
  if (counters.length === 0) return null;
  return <CounterList counters={counters} label={label} style={style} />;
}

function CounterList({ counters, label, style }: { counters: CounterView[]; label: string; style: 'chips' | 'table' | 'meters' }) {
  const api = usePresentation();
  return (
    <dl className={`counter-strip counter-style-${style}`} aria-label={label}>
      {counters.map(c => {
        const resourceKey = c.key?.startsWith('resource:') ? c.key.slice(9) : c.key;
        const icon = resourceKey ? api.icon(resourceKey) : null;
        const numeric = typeof c.value === 'number' ? c.value : null;
        return (
          <div key={c.id} className="counter">
            <dt>{icon && <span className="counter-icon" aria-hidden="true"><Slot value={icon} /></span>}{c.label}</dt>
            <dd>{String(c.value)}{c.max !== null ? <span className="counter-max">/{c.max}</span> : null}</dd>
            {style === 'meters' && c.max !== null && numeric !== null && (
              <span className="counter-meter" aria-hidden="true"><span style={{ width: `${Math.max(0, Math.min(100, (numeric / c.max) * 100))}%` }} /></span>
            )}
          </div>
        );
      })}
    </dl>
  );
}
